import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Badge,
  EmptyState,
  FilterChip,
  QueryStateBoundary,
  ConfirmDialog,
} from "@/components/ui";
import { useContacts, useDeleteContact } from "@/api/hooks/useContacts";
import {
  CONTACT_KINDS,
  CONTACT_KIND_LABELS,
  type Contact,
  type ContactKind,
  type ContactWarm,
} from "@/api/types/contact";
import { ContactFormDialog } from "./ContactFormDialog";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { getErrorMessage } from "@/api/client";

type KindFilter = ContactKind | "all";
type WarmFilter = ContactWarm | "all";

export function ContactsPage() {
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [warmFilter, setWarmFilter] = useState<WarmFilter>("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useContacts({
    kind: kindFilter === "all" ? undefined : kindFilter,
    warm: warmFilter === "all" ? undefined : warmFilter,
    q: search,
  });

  const deleteContact = useDeleteContact();

  const contacts = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  function openCreate() {
    setEditingContact(null);
    setFormOpen(true);
  }

  function openEdit(contact: Contact) {
    setEditingContact(contact);
    setFormOpen(true);
  }

  async function confirmDelete() {
    if (!deletingContact) return;
    try {
      await deleteContact.mutateAsync(deletingContact.id);
      toastSuccess("Contact deleted");
    } catch (err) {
      toastError("Couldn't delete contact", getErrorMessage(err));
    } finally {
      setDeletingContact(null);
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-text-primary font-display">Contacts</h1>
        <Button onClick={openCreate}>Add contact</Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts by name, company, email..."
          aria-label="Search contacts"
          className="w-full sm:max-w-md h-10 rounded-md border border-border bg-bg-card px-3 py-2 text-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <FilterChip label="All kinds" active={kindFilter === "all"} onClick={() => setKindFilter("all")} />
        {CONTACT_KINDS.map((kind) => (
          <FilterChip
            key={kind}
            label={CONTACT_KIND_LABELS[kind]}
            active={kindFilter === kind}
            onClick={() => setKindFilter(kind)}
          />
        ))}
        <span className="mx-1 w-px bg-border" />
        <FilterChip label="All" active={warmFilter === "all"} onClick={() => setWarmFilter("all")} />
        <FilterChip label="Warm" active={warmFilter === "warm"} onClick={() => setWarmFilter("warm")} />
        <FilterChip label="Cold" active={warmFilter === "cold"} onClick={() => setWarmFilter("cold")} />
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {contacts.length === 0 ? (
          <EmptyState
            icon="📇"
            title="No contacts yet"
            description="Add your first host, planner, venue, corporate, or vendor contact to get started."
            action={{ label: "Add your first contact", onClick: openCreate }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-muted/50 text-left">
                  <th className="px-4 py-3 font-medium text-text-secondary">Name</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Kind</th>
                  <th className="px-4 py-3 font-medium text-text-secondary hidden sm:table-cell">
                    Contact
                  </th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Warm</th>
                  <th className="px-4 py-3 font-medium text-text-secondary hidden md:table-cell">
                    Tags
                  </th>
                  <th className="px-4 py-3 font-medium text-text-secondary text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    data-contact-id={contact.id}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-bg-hover"
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{contact.name}</p>
                      {contact.company && (
                        <p className="text-xs text-text-muted">{contact.company}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{CONTACT_KIND_LABELS[contact.kind]}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-text-secondary">
                      {contact.email && <div>{contact.email}</div>}
                      {contact.phone && <div>{contact.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={contact.warm === "warm" ? "success" : "default"}>
                        {contact.warm === "warm" ? "Warm" : "Cold"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center rounded-full bg-bg-muted px-2 py-0.5 text-xs text-text-secondary"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(contact);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingContact(contact);
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      {hasNextPage && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}

      <ContactFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        contact={editingContact}
      />

      <ConfirmDialog
        open={!!deletingContact}
        onClose={() => setDeletingContact(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete contact"
        message={`Delete ${deletingContact?.name ?? "this contact"}? This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteContact.isPending}
      />
    </div>
  );
}
